/**
 * Centro Pokémon de Saffron: encaixa paredes/portas sob o teto.
 * - Paredes N: +1 S | Paredes O: +1 E
 * - Só móveis na faixa colada a essas paredes acompanham o nudge
 * - Chão nunca se move (exceto troca asfalto→passeio na fachada N/O)
 * - Táxi + placa: não desenhar / não colidir
 * - Arbusto 13182 no canto NO: não desenhar
 * - Colisão: blocking items usam o mesmo nudge visual
 *
 * O desencontro é do próprio OTBM, não dos antigos overlays WebP: no mapa, as
 * paredes N/O do prédio ficam 1 tile fora da pegada do telhado. Medindo os
 * andares extraídos, o telhado (z=5) ocupa x 1029–1044 / y 1021–1037 e as
 * paredes nascem em x 1026–1029 / y 1020–1021 — o empurrão de 1 tile é o que
 * põe as duas coisas na mesma pegada. Removê-lo deixa as paredes de cima e da
 * esquerda para fora do teto.
 */

const TAXI_IDS = new Set([
  13343,
  13360, 13361, 13362, 13363,
  13364, 13365, 13366, 13367,
  13368, 13369, 13370, 13371,
]);

const SKIP_NEAR_PC_IDS = new Set([13182]);

const NORTH_WALL_IDS = new Set([
  26667, 26661, 26659, 26660, 26663, 27051,
  5771, 26818, 26819, 11425,
  12839, 12840, 12842, 13193,
]);

const WEST_WALL_IDS = new Set([
  26658, 26661, 26660, 26664, 26666,
  11423, 5773, 26820, 26821,
  27209, 27210,
]);

const NORTH_FURNITURE_BAND = { minX: 1028, maxX: 1045, minY: 1020, maxY: 1022 };
const WEST_FURNITURE_BAND = { minX: 1028, maxX: 1030, minY: 1020, maxY: 1037 };

/** Asfalto (11665) sob a fachada N/O → passeio, espelhando S/L. Portas ficam asfalto. */
const ASPHALT = 11665;
const PC_ASPHALT_TO_SIDEWALK = new Map([
  // Norte y=1020 (portas 1032–1033 e 1041–1042 ficam asfalto)
  ['1029,1020', 11645],
  ['1030,1020', 11645],
  ['1031,1020', 11645],
  ['1034,1020', 11654],
  ['1035,1020', 11654],
  ['1036,1020', 11645],
  ['1037,1020', 11655],
  ['1038,1020', 11645],
  ['1039,1020', 11645],
  ['1040,1020', 11645],
  ['1043,1020', 11645],
  ['1044,1020', 11645],
  // Oeste x=1028 (portas y=1026–1027 e 1033–1034 ficam asfalto)
  ['1028,1021', 11645],
  ['1028,1022', 11645],
  ['1028,1023', 11645],
  ['1028,1024', 11645],
  ['1028,1025', 11645],
  ['1028,1028', 11645],
  ['1028,1029', 11645],
  ['1028,1030', 11645],
  ['1028,1031', 11645],
  ['1028,1032', 11647],
  ['1028,1035', 11645],
  ['1028,1036', 11645],
]);

export function isPcTaxiItem(serverId) {
  return TAXI_IDS.has(serverId);
}

export function isPcSkippedItem(serverId, x, y) {
  if (TAXI_IDS.has(serverId)) return true;
  if (
    SKIP_NEAR_PC_IDS.has(serverId) &&
    x >= 1028 &&
    x <= 1032 &&
    y >= 1020 &&
    y <= 1025
  ) {
    return true;
  }
  return false;
}

/** Troca asfalto da fachada N/O por passeio (igual S/L). */
export function pcGroundId(x, y, groundId) {
  if (groundId !== ASPHALT) return groundId;
  return PC_ASPHALT_TO_SIDEWALK.get(`${x},${y}`) || groundId;
}

/** O piso jogável e a parede/objeto logo abaixo do telhado compartilham o alinhamento. */
export function shouldNudgePcWallFloor(z, groundZ) {
  return Number.isFinite(Number(z)) && Number.isFinite(Number(groundZ))
    && (Number(z) === Number(groundZ) || Number(z) === Number(groundZ) - 1);
}

/**
 * Aplica o alinhamento do Centro Pokémon somente aos pisos que ficam sob o
 * telhado. O piso do telhado não pode ser deslocado junto com seus objetos.
 */
export function pcWallNudgeForFloor(serverId, x, y, z, groundZ, opts = {}) {
  if (!shouldNudgePcWallFloor(z, groundZ)) return { dx: 0, dy: 0 };
  return pcWallDrawNudge(serverId, x, y, opts);
}

/**
 * @param {number} serverId
 * @param {number} x
 * @param {number} y
 * @param {{ isGround?: boolean }} [opts]
 * @returns {{ dx: number, dy: number }}
 */
export function pcWallDrawNudge(serverId, x, y, opts = {}) {
  if (opts.isGround) return { dx: 0, dy: 0 };

  let dx = 0;
  let dy = 0;

  const isNorthWall =
    NORTH_WALL_IDS.has(serverId) && y >= 1020 && y <= 1021 && x >= 1028 && x <= 1045;
  const isWestWall =
    WEST_WALL_IDS.has(serverId) && x >= 1026 && x <= 1029 && y >= 1020 && y <= 1037;

  if (isNorthWall) dy = 1;
  if (isWestWall) dx = 1;

  if (!isNorthWall && !isWestWall) {
    const inNorthBand =
      x >= NORTH_FURNITURE_BAND.minX &&
      x <= NORTH_FURNITURE_BAND.maxX &&
      y >= NORTH_FURNITURE_BAND.minY &&
      y <= NORTH_FURNITURE_BAND.maxY;
    const inWestBand =
      x >= WEST_FURNITURE_BAND.minX &&
      x <= WEST_FURNITURE_BAND.maxX &&
      y >= WEST_FURNITURE_BAND.minY &&
      y <= WEST_FURNITURE_BAND.maxY;
    if (inNorthBand) dy = 1;
    if (inWestBand) dx = 1;
  }

  return { dx, dy };
}
